/**
 * NaatiClusterLinks.tsx — Wave 39
 *
 * SSR-rendered, same-language contextual link block for the NAATI cluster.
 * Detects locale + current path from the router, links to every sibling NAATI
 * hub that exists as a real route (no 404s), and adds the mirror-language
 * counterpart so crawlers can walk TH ↔ EN.
 *
 * Mounted once inside NaatiKnowledgePage plus the four NAATI index routes,
 * so all NAATI pages gain 8+ contextual outbound links without per-route edits.
 */
import { Link, useRouterState } from "@tanstack/react-router";

type Loc = "th" | "en";

const SLUGS = [
  "",
  "levels",
  "interpreting",
  "ccl-test-prep",
  "online",
  "au",
  "city",
  "faq",
  "visa",
] as const;

const LABELS: Record<Loc, Record<string, string>> = {
  th: {
    "": "NAATI แปลรับรองออสเตรเลีย (หน้าหลัก)",
    levels: "ระดับการรับรอง NAATI (CT · CAT · CPI · CI)",
    interpreting: "ล่าม NAATI (ศาล · โรงพยาบาล · ธุรกิจ)",
    "ccl-test-prep": "ติวสอบ CCL รับ 5 คะแนน PR",
    online: "แปล NAATI ออนไลน์ ส่งทั่วโลก",
    au: "NAATI ตามเมืองในออสเตรเลีย",
    city: "NAATI ตามจังหวัดในไทย",
    faq: "คลังคำถาม NAATI",
    visa: "NAATI ตามประเภทวีซ่า (Subclass)",
  },
  en: {
    "": "NAATI Certified Translation (hub)",
    levels: "NAATI certification levels (CT · CAT · CPI · CI)",
    interpreting: "NAATI interpreting (court · hospital · business)",
    "ccl-test-prep": "CCL test prep — the 5-point PR bonus",
    online: "Online NAATI translation, delivered worldwide",
    au: "NAATI by Australian city",
    city: "NAATI by Thai province",
    faq: "NAATI question bank",
    visa: "NAATI by visa subclass",
  },
};

const HEADING: Record<Loc, string> = {
  th: "หัวข้อ NAATI ที่เกี่ยวข้อง",
  en: "Related NAATI topics",
};

const MIRROR: Record<Loc, string> = {
  th: "อ่านหน้านี้เป็นภาษาอังกฤษ",
  en: "อ่านหน้านี้ภาษาไทย (Thai version)",
};

function base(locale: Loc) {
  return locale === "th" ? "/translate/naati" : "/en/translate/naati";
}

export default function NaatiClusterLinks() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const clean = pathname.replace(/\/+$/, "");
  const locale: Loc = clean.startsWith("/en/") ? "en" : "th";
  const root = base(locale);
  if (!clean.startsWith(root)) return null;

  const rest = clean.slice(root.length).replace(/^\//, "");
  const current = rest.split("/")[0] ?? "";
  const labels = LABELS[locale];
  const mirrorRoot = base(locale === "th" ? "en" : "th");
  const mirrorHref = rest ? `${mirrorRoot}/${rest}` : mirrorRoot;

  return (
    <nav
      aria-label={HEADING[locale]}
      className="mx-auto mt-14 max-w-4xl border-t border-slate-200 pt-8"
    >
      <h2 className="font-display text-xl font-extrabold text-navy-deep">{HEADING[locale]}</h2>
      <ul className="mt-4 grid gap-2 sm:grid-cols-2">
        {SLUGS.filter((s) => s !== current).map((s) => (
          <li key={s || "root"}>
            <Link
              to={(s ? `${root}/${s}` : root) as never}
              className="block rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-amber-500 hover:text-navy-deep"
            >
              {labels[s]}
            </Link>
          </li>
        ))}
        <li>
          <Link
            to={mirrorHref as never}
            className="block rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm font-medium text-slate-700 transition hover:border-amber-500 hover:text-navy-deep"
          >
            {MIRROR[locale]}
          </Link>
        </li>
      </ul>
    </nav>
  );
}
